import { React } from 'react'
import { motion } from 'framer-motion'
import { useDocumentTitle, useDocumentConfig } from 'hooks/useDocument'
import Hero from 'modules/Hero'
import Button from 'components/Button'
import ServicesList from 'components/ServicesList'
import './ServicesPages.scss'

const IntegrationAnimation = () => {
	useDocumentTitle(`Intégration & Animation Web - Développement Front-End Expert`)
	useDocumentConfig(`blue`, false)

	return (
		<motion.main
			className='service-page'
			initial={{ opacity: 0, transition: { duration: 0.3 } }}
			animate={{ opacity: 1 }}
			exit={{ opacity: 0, transition: { duration: 0.3 } }}
		>
			<Hero
				scene={4}
				scrollTo='Découvrir nos solutions'
			>
				Intégration &<br />
				Animation
			</Hero>

			<section className='service-intro'>
				<div className='container'>
					<h1 className='align-center'>Intégration & Animation Web</h1>
					<p className='lead align-center'>
						Transformez vos designs en expériences web exceptionnelles avec des animations fluides et des interactions innovantes
					</p>
				</div>
			</section>

			<section className='service-content'>
				<div className='container'>
					<div className='content-grid'>
						<div className='content-main'>
							<h2>Des interfaces web qui captivent et convertissent</h2>
							<p>
								Notre expertise en intégration front-end et animation web vous garantit des sites performants, 
								accessibles et visuellement impressionnants. Nous transformons vos maquettes en réalité interactive, 
								en respectant scrupuleusement votre identité visuelle tout en optimisant l'expérience utilisateur.
							</p>

							<h3>Notre approche technique</h3>
							<ul className='no-prefix'>
								<li><strong>Intégration pixel-perfect</strong> : Respect minutieux de vos maquettes (Figma, Adobe XD, Sketch)</li>
								<li><strong>Performance optimale</strong> : Code optimisé pour des temps de chargement ultra-rapides</li>
								<li><strong>Animations sur-mesure</strong> : Transitions fluides et micro-interactions engageantes</li>
								<li><strong>Responsive design avancé</strong> : Adaptation parfaite sur tous les écrans et dispositifs</li>
								<li><strong>Accessibilité WCAG</strong> : Sites utilisables par tous, conformes aux standards d'accessibilité</li>
							</ul>

							<h3>Technologies maîtrisées</h3>
							<div className='tech-grid'>
								<div className='tech-category'>
									<h4>Frameworks JavaScript</h4>
									<p>React, Vue.js, Next.js, Nuxt.js, Gatsby</p>
								</div>
								<div className='tech-category'>
									<h4>Librairies d'animation</h4>
									<p>GSAP, Anime.js, Framer Motion, Three.js, Lottie</p>
								</div>
								<div className='tech-category'>
									<h4>Outils de build</h4>
									<p>Webpack, Vite, Parcel, esbuild</p>
								</div>
								<div className='tech-category'>
									<h4>CSS avancé</h4>
									<p>Sass/SCSS, PostCSS, Tailwind CSS, CSS-in-JS</p>
								</div>
							</div>

							<h3>Types de projets réalisés</h3>
							<ul className='no-prefix'>
								<li>Sites vitrines avec animations immersives</li>
								<li>Landing pages haute conversion</li>
								<li>Portfolios créatifs et galeries interactives</li>
								<li>Dashboards et interfaces data-driven</li>
								<li>Expériences WebGL et 3D interactives</li>
								<li>Progressive Web Apps (PWA)</li>
							</ul>
						</div>

						<aside className='content-sidebar'>
							<div className='sidebar-box'>
								<h3>Pourquoi nous choisir ?</h3>
								<ul className='benefits-list no-prefix'>
									<li>10+ ans d'expertise front-end</li>
									<li>Code propre et maintenable</li>
									<li>Respect des délais garantis</li>
									<li>Communication transparente</li>
									<li>Formation et documentation incluses</li>
								</ul>
							</div>

							<div className='sidebar-box'>
								<h3>Tarifs indicatifs</h3>
								<p className='price-info'>
									<strong>TJM : 450-500€ HT</strong><br />
									Selon complexité du projet
								</p>
								<p className='small'>
									Devis gratuit sous 48h<br />
									Forfaits disponibles pour projets longs
								</p>
							</div>

							<div className='sidebar-box cta-box'>
								<h3>Prêt à démarrer ?</h3>
								<p>Discutons de votre projet d'intégration et animation web</p>
								<Button to={`${process.env.PUBLIC_URL}/nous-contacter`} version='1' size='small'>
									Demander un devis
								</Button>
							</div>
						</aside>
					</div>
				</div>
			</section>

			<section className='service-process'>
				<div className='container'>
					<h2 className='align-center'>Notre processus de travail</h2>
					<div className='process-steps'>
						<div className='process-step'>
							<span className='step-number'>01</span>
							<h3>Analyse</h3>
							<p>Étude de vos maquettes et spécifications techniques</p>
						</div>
						<div className='process-step'>
							<span className='step-number'>02</span>
							<h3>Architecture</h3>
							<p>Structuration modulaire et choix technologiques adaptés</p>
						</div>
						<div className='process-step'>
							<span className='step-number'>03</span>
							<h3>Intégration</h3>
							<p>Développement front-end avec animations et interactions</p>
						</div>
						<div className='process-step'>
							<span className='step-number'>04</span>
							<h3>Optimisation</h3>
							<p>Performance, SEO et tests multi-navigateurs</p>
						</div>
					</div>
				</div>
			</section>

			<ServicesList currentService='integration-animation' />

			<div className='column-full align-center'>
				<Button to={`${process.env.PUBLIC_URL}/nous-contacter`} version='1'>
					Démarrer votre projet
				</Button>
			</div>
		</motion.main>
	)
}

export default IntegrationAnimation